"use client"

import * as React from "react"
import { FolderOpen } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface MemberWorkspaceCellProps {
  member: any
  allWorkspaces: any[]
  userWorkspacesMap: Record<string, string[]>
  fetchUserWorkspaces: (userId: string) => void
  handleToggleUserWorkspace?: (userId: string, workspaceId: string, isAssigned: boolean) => void
}

export function MemberWorkspaceCell({
  member,
  allWorkspaces,
  userWorkspacesMap,
  fetchUserWorkspaces,
}: MemberWorkspaceCellProps) {
  const memberRole = member.accountRole || member.companyRole || member.role
  const isOwnerOrAdmin = memberRole === "owner" || memberRole === "admin"

  React.useEffect(() => {
    if (!isOwnerOrAdmin && member.userId && userWorkspacesMap[member.userId] === undefined) {
      fetchUserWorkspaces(member.userId)
    }
  }, [member.userId, isOwnerOrAdmin, userWorkspacesMap, fetchUserWorkspaces])

  const memberWsIds = userWorkspacesMap[member.userId] || []
  const assignedWorkspaces = React.useMemo(() => {
    if (isOwnerOrAdmin) {
      return allWorkspaces
    }
    return allWorkspaces.filter((w) => memberWsIds.includes(w.id))
  }, [isOwnerOrAdmin, memberWsIds, allWorkspaces])

  if (assignedWorkspaces.length === 0) {
    return (
      <span className="text-[10px] text-zinc-400 italic">
        Sem workspaces vinculados
      </span>
    )
  }

  const VISIBLE_LIMIT = 3
  const visibleWorkspaces = assignedWorkspaces.slice(0, VISIBLE_LIMIT)
  const remainingWorkspaces = assignedWorkspaces.slice(VISIBLE_LIMIT)

  return (
    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
      {visibleWorkspaces.map((ws) => (
        <span
          key={ws.id}
          title={ws.name}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50/80 dark:bg-blue-955/30 text-[#004EB1] dark:text-blue-300 border border-blue-200/70 dark:border-blue-900/40 max-w-[110px] truncate"
        >
          <FolderOpen className="size-2.5 shrink-0 text-[#004EB1] dark:text-blue-400" />
          <span className="truncate">{ws.name}</span>
        </span>
      ))}

      {remainingWorkspaces.length > 0 && (
        <Tooltip>
          <TooltipTrigger className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700 cursor-pointer transition-colors shrink-0">
            +{remainingWorkspaces.length}
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="z-50 p-2.5 rounded-lg shadow-xl text-xs flex flex-col gap-1.5 max-w-xs"
          >
            <div className="text-[10px] font-bold opacity-75 uppercase tracking-wider border-b border-background/20 pb-1">
              Outros workspaces ({remainingWorkspaces.length})
            </div>
            <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto pr-1">
              {remainingWorkspaces.map((ws) => (
                <div key={ws.id} className="flex items-center gap-1.5 text-xs">
                  <FolderOpen className="size-3 opacity-80 shrink-0" />
                  <span className="truncate font-medium">{ws.name}</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
